import { useEffect, useState } from 'react';
import { Badge, Card, Empty, Sheet, Spinner, api, brl, dateTime, km } from '@mobi/web';
import type { DriverRide } from '../useDriver.js';

interface HistoryRide extends DriverRide {
  timestamps: Record<string, string | null>;
}

/**
 * Corridas de uma modalidade, no mesmo período que o resumo estava
 * mostrando. Reusa o filtro que já existe em `GET /api/rides` — nenhuma
 * rota nova, só os parâmetros que o motorista pediu para enxergar.
 */
export function PaymentRides({
  method,
  methodLabel,
  from,
  to,
  onClose,
}: {
  method: string;
  methodLabel: string;
  from: string;
  to: string;
  onClose: () => void;
}) {
  const [rides, setRides] = useState<HistoryRide[] | null>(null);

  useEffect(() => {
    setRides(null);
    api<{ rides: HistoryRide[] }>(
      `/api/rides?paymentMethod=${method}&from=${from}&to=${to}&limit=200`,
    )
      .then((r) => setRides(r.rides))
      .catch(() => setRides([]));
  }, [method, from, to]);

  return (
    <div
      className="fixed inset-0 z-30 flex flex-col justify-end bg-black/50"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()}>
        <Sheet className="max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">{methodLabel}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              className="grid h-9 w-9 place-items-center rounded-full bg-surface-2 text-lg"
            >
              ✕
            </button>
          </div>

          {!rides && (
            <div className="mt-8 grid place-items-center text-brand">
              <Spinner />
            </div>
          )}

          {rides?.length === 0 && (
            <div className="mt-6">
              <Empty title="Nenhuma corrida nesse período" />
            </div>
          )}

          <ul className="mt-4 space-y-3">
            {rides?.map((ride) => (
              <li key={ride.id}>
                <Card className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{ride.destination.address}</p>
                      <p className="truncate text-sm text-muted">de {ride.origin.address}</p>
                      <p className="mt-1 text-xs text-faint">
                        {dateTime(ride.timestamps.finishedAt)} · {km(ride.estimate.distanceMeters)}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="tabular font-semibold">{brl(ride.cashDueCents ?? 0)}</p>
                      {ride.passenger && (
                        <Badge tone="neutral">{ride.passenger.name.split(' ')[0]}</Badge>
                      )}
                    </div>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </Sheet>
      </div>
    </div>
  );
}
